import { useEffect, useState } from "react";
import { Save, RefreshCw } from "lucide-react";
import { Select } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import {
  PLAN_SERVICES_URL,
  QUOTATION_MODULES_URL,
} from "../../constants/api";

const PRODUCTS = [
  { key: "cms",  label: "Clinical Management System" },
  { key: "hims", label: "Hospital Information Management System" },
];

const PLANS = [
  { key: "essential",  label: "Essential" },
  { key: "advanced",   label: "Advanced" },
  { key: "enterprise", label: "Enterprise" },
];

export default function PlanServices() {
  const { fetch, update } = useApiMethods();

  // services: { [product]: { [plan]: [module_id, ...] } }
  const [services, setServices]         = useState({});
  const [modules, setModules]           = useState([]);
  const [activeProduct, setActiveProduct] = useState("cms");
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [includeRoadmap, setIncludeRoadmap] = useState(false);

  const load = async () => {
    setLoading(true);
    const [res, modRes] = await Promise.all([
      fetch(PLAN_SERVICES_URL),
      fetch(QUOTATION_MODULES_URL),
    ]);
    setLoading(false);
    if (res?.status !== "success") return ToastMsg(res?.message || "Failed to load", "error");
    if (modRes?.status === "success") setModules(modRes.data || []);

    // Initialize empty structure
    const grouped = {};
    PRODUCTS.forEach(p => {
      grouped[p.key] = {};
      PLANS.forEach(pl => { grouped[p.key][pl.key] = []; });
    });
    // Fill from API
    (res.data || []).forEach(row => {
      if (grouped[row.product]?.[row.plan] !== undefined) {
        grouped[row.product][row.plan] = row.module_ids || [];
      }
    });
    setServices(grouped);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setModuleIds = (product, plan, ids) => {
    setServices(prev => ({
      ...prev,
      [product]: { ...(prev[product] || {}), [plan]: ids },
    }));
  };

  const save = async () => {
    setSaving(true);
    const mappings = [];
    PRODUCTS.forEach(p => {
      PLANS.forEach(pl => {
        mappings.push({
          product: p.key,
          plan: pl.key,
          module_ids: services?.[p.key]?.[pl.key] || [],
        });
      });
    });
    const res = await update(PLAN_SERVICES_URL, { mappings });
    setSaving(false);
    if (res?.status === "success") {
      ToastMsg("Plan services saved", "success");
    } else {
      ToastMsg(res?.message || "Failed to save", "error");
    }
  };

  const activeServices = services?.[activeProduct] || {};

  if (loading && !Object.keys(services).length) {
    return <div className="p-6 text-sm text-gray-500">Loading...</div>;
  }

  return (
    <ContainerCard>
      <div className="px-6 py-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Plan Services</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Map default modules, features, and add-ons to each plan. These are pre-filled when creating a quotation.
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={load}>
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" onClick={save} disabled={saving}>
              <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        {/* Product tabs */}
        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
          {PRODUCTS.map(p => (
            <button
              key={p.key}
              onClick={() => setActiveProduct(p.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition
                ${activeProduct === p.key
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Roadmap toggle */}
        <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={includeRoadmap}
            onChange={(e) => setIncludeRoadmap(e.target.checked)}
            className="h-3 w-3"
          />
          Show roadmap items
        </label>

        {/* Service mapping table */}
        <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900/50">
                {PLANS.map(pl => (
                  <th key={pl.key} className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white w-1/3">
                    {pl.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {PLANS.map(pl => {
                  const selectedIds = activeServices?.[pl.key] || [];
                  const visible = modules.filter(m =>
                    includeRoadmap || m.is_available !== false || selectedIds.includes(m.id)
                  );
                  const group = (type) => visible.filter(m => (m.catalog_type || "module") === type);
                  const mkOption = (m) => ({
                    value: m.id,
                    label: `${m.name}${m.txn_per_visit > 0 ? ` - ${m.txn_per_visit} txn/visit` : ""}${m.is_available === false ? " - (roadmap)" : ""}`,
                  });
                  const groupedOptions = [
                    { label: `Modules (${group("module").length})`,   options: group("module").map(mkOption)   },
                    { label: `Features (${group("feature").length})`, options: group("feature").map(mkOption) },
                    { label: `Add-ons (${group("addon").length})`,    options: group("addon").map(mkOption)    },
                  ].filter(g => g.options.length);

                  const roadmapCount = selectedIds.filter(id =>
                    modules.find(m => m.id === id)?.is_available === false
                  ).length;
                  const txnSum = selectedIds.reduce((s, id) =>
                    s + Number(modules.find(m => m.id === id)?.txn_per_visit || 0), 0
                  );

                  return (
                    <td key={pl.key} className="px-4 py-3 align-top border-r border-gray-100 dark:border-gray-700 last:border-r-0">
                      <Select
                        mode="multiple"
                        className="w-full"
                        placeholder="Pick modules, features, add-ons..."
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        value={selectedIds}
                        onChange={(ids) => setModuleIds(activeProduct, pl.key, ids)}
                        options={groupedOptions}
                        style={{ minHeight: 200 }}
                      />
                      <div className="text-[10px] text-gray-400 mt-2 tabular-nums flex items-center gap-3 flex-wrap">
                        <span>{selectedIds.length} selected</span>
                        <span>Txn/visit: {txnSum}</span>
                        {roadmapCount > 0 && (
                          <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                            {roadmapCount} roadmap
                          </span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </ContainerCard>
  );
}
